import { CloseButton, Combobox, Input, InputBase, useCombobox, Text, ActionIcon } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import cn from 'classnames';
import { observer } from 'mobx-react-lite';
import * as React from 'react';

import AbsoluteError from '@/components/ui/AbsoluteError';
import { FieldSelectStore } from '@/stores/local/shared/form/FieldSelectStore';
import { useDevices } from '@/utils/devices';
import { preventDefault } from '@/utils/preventDefault';

import OptionsDropdown from './OptionsDropdown';

import s from './FieldSelect.module.scss';

type FieldSelectProps = {
  className?: string;
  placeholder?: string;
  label?: React.ReactNode;
  size?: string;
  store: FieldSelectStore<any>;
  fw?: string | number;
  placeholderClassName?: string;
  disabled?: boolean;
  withSearch?: boolean;
  withCloseButton?: boolean;
  afterSlot?: React.ReactNode;
  hideSubtitle?: boolean;
};

const FieldSelect: React.FC<FieldSelectProps> = ({
  store,
  placeholder,
  label,
  className,
  fw = 500,
  size,
  placeholderClassName,
  disabled,
  withSearch = false,
  withCloseButton = false,
  afterSlot,
  hideSubtitle = false,
}) => {
  const { isCompactViewport } = useDevices();

  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      combobox.focusTarget();
      store.blur();
      store.resetSearch();
    },

    onDropdownOpen: () => {
      store.focus();
      combobox.focusSearchInput();
    },
  });

  const onInputClick = React.useCallback(() => {
    if (disabled) {
      return;
    }

    combobox.toggleDropdown();
  }, [combobox, disabled]);

  const handleBlur = React.useCallback(() => {
    if (combobox.dropdownOpened) {
      return;
    }

    store.blur();
  }, [combobox, store]);

  const onOptionSubmit = React.useCallback(
    (value: string) => {
      store.setValueByKey(value);
      combobox.closeDropdown();
    },
    [store, combobox],
  );

  const active = store.value?.[0] ?? null;

  return (
    <div className={className}>
      <Combobox
        variant="default"
        store={combobox}
        keepMounted={false}
        withinPortal={true}
        onOptionSubmit={onOptionSubmit}
      >
        <Combobox.Target>
          <InputBase
            withAsterisk={store.required}
            label={label}
            error={store.hasError && <AbsoluteError error={store.error} />}
            size={size ?? (isCompactViewport ? 'md' : 'lg')}
            component="button"
            type="button"
            pointer
            onClick={onInputClick}
            disabled={disabled}
            onFocus={store.focus}
            onBlur={handleBlur}
            rightSectionPointerEvents={disabled ? 'none' : active ? undefined : 'none'}
            rightSection={
              active && withCloseButton ? (
                <CloseButton
                  c="gray"
                  size="lg"
                  disabled={disabled}
                  onMouseDown={preventDefault}
                  onClick={store.resetValue}
                />
              ) : afterSlot ? (
                <ActionIcon variant="transparent" onClick={onInputClick}>
                  {afterSlot}
                </ActionIcon>
              ) : (
                <ActionIcon variant="transparent" c="gray" onClick={onInputClick}>
                  <IconChevronDown className={cn(s.root__icon, combobox.dropdownOpened && s.root__icon_opened)} />
                </ActionIcon>
              )
            }
            classNames={{
              root: s.root,
              input: s.root__input,
            }}
            styles={{
              label: {
                fontWeight: fw,
              },
              input: {
                display: 'flex',
                alignItems: 'center',
              },
            }}
          >
            {active?.value ? (
              <Text className={s.root__value} size="sm" lineClamp={1} fw={400}>
                {active.value}
              </Text>
            ) : (
              placeholder && (
                <Input.Placeholder className={cn(s.root__placeholder, placeholderClassName)}>
                  {placeholder}
                </Input.Placeholder>
              )
            )}
          </InputBase>
        </Combobox.Target>

        <OptionsDropdown withSearch={withSearch} store={store} hideSubtitle={hideSubtitle} />
      </Combobox>
    </div>
  );
};

export default observer(FieldSelect);
